<template>
	<div>
		<Head :title="__('Dashboard')" />

		<h1 class="mb-8 text-3xl font-bold">{{ __('Dashboard') }}</h1>

		<p class="mb-8 leading-normal">
			{{ __('Hey there! Welcome to Pfizershop. We\'re glad you\'re here.') }}
		</p>

		<div class=" grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2">
			<div>
				<span class="relative z-0 inline-flex shadow-sm rounded-md">
					<button
						type="button"
						class="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 text-sm font-medium text-gray-700 focus:z-10 focus:outline-none"
						:class="{ 'bg-[#1d3557] text-white': topProductsDays === 7 }"
						@click="setTopProductsDays(7)"
					>
						{{ __('7 Days') }}
					</button>

					<button
						type="button"
						class="-ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 focus:z-10 focus:outline-none"
						:class="{ 'bg-[#1d3557] text-white': topProductsDays === 30 }"
						@click="setTopProductsDays(30)"
					>
						{{ __('30 Days') }}
					</button>

					<button
						type="button"
						class="-ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 focus:z-10 focus:outline-none"
						:class="{ 'bg-[#1d3557] text-white': topProductsDays === 60 }"
						@click="setTopProductsDays(60)"
					>
						{{ __('60 Days') }}
					</button>

					<button
						type="button"
						class="-ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 focus:z-10 focus:outline-none"
						:class="{ 'bg-[#1d3557] text-white': topProductsDays === 90 }"
						@click="setTopProductsDays(90)"
					>
						{{ __('90 Days') }}
					</button>
				</span>

				<dl class="mt-5 grid">
					<div class="relative bg-white pt-5 px-4 pb-12 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden">
						<dt>
							<h3 class="font-bold mb-6">{{ __('Top 5 Products') }}</h3>
						</dt>

						<dd v-if="!topProducts.length">
							<p class="ml-6 text-lg font-medium text-gray-500 truncate">
								{{ __(' No Data!') }}
							</p>
						</dd>

						<dd>
							<Doughnut
								:chart-data="topProductsChartData.chart"
								:chart-options="topProductsChartData.options"
							/>
						</dd>

						<dd class="ml-6 pt-6 pb-6 flex flex-col items-baseline sm:pb-7">
							<p
								v-for="(item, index) in topProducts"
								class="text-xl text-gray-900"
								:key="index"
							>
								<Link :href="`/products/${item.product_id}/edit`">
									- {{ item.name }}, {{ item.amount }} st
								</Link>
							</p>

							<div class="absolute bottom-0 inset-x-0 bg-gray-50 px-4 py-4 sm:px-6 justify-end">
								<div class="text-sm">
									<a href="/products" class="font-medium text-[#45618a] hover:text-[#142b4c]">
										Till produktlistan »
										<span class="sr-only"> {{ __('Products') }}</span>
									</a>
								</div>
							</div>
						</dd>
					</div>
				</dl>
			</div>

			<div>
				<span class="relative z-0 inline-flex shadow-sm rounded-md">
					<button
						type="button"
						class="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 text-sm font-medium text-gray-700 focus:z-10 focus:outline-none"
						:class="{ 'bg-[#1d3557] text-white': topDownloadsDays === 7 }"
						@click="setTopDownloadsDays(7)"
					>
						{{ __('7 Days') }}
					</button>

					<button
						type="button"
						class="-ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 focus:z-10 focus:outline-none"
						:class="{ 'bg-[#1d3557] text-white': topDownloadsDays === 30 }"
						@click="setTopDownloadsDays(30)"
					>
						{{ __('30 Days') }}
					</button>

					<button
						type="button"
						class="-ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 focus:z-10 focus:outline-none"
						:class="{ 'bg-[#1d3557] text-white': topDownloadsDays === 60 }"
						@click="setTopDownloadsDays(60)"
					>
						{{ __('60 Days') }}
					</button>

					<button
						type="button"
						class="-ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 focus:z-10 focus:outline-none"
						:class="{ 'bg-[#1d3557] text-white': topDownloadsDays === 90 }"
						@click="setTopDownloadsDays(90)"
					>
						{{ __('90 Days') }}
					</button>
				</span>

				<dl class="mt-5 grid">
					<div class="relative bg-white pt-5 px-4 pb-12 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden">
						<dt>
							<h3 class="font-bold mb-6">{{ __('Top 5 Downloads') }}</h3>
						</dt>

						<dd v-if="!topDownloads.length">
							<p class="ml-6 text-lg font-medium text-gray-500 truncate">
								{{ __(' No Data!') }}
							</p>
						</dd>

						<dd>
							<Doughnut
								:chart-data="topDownloadsChartData.chart"
								:chart-options="topDownloadsChartData.options"
							/>
						</dd>

						<dd class="ml-6 pt-6 pb-6 flex flex-col items-baseline sm:pb-7">
							<p
								v-for="(item, index) in topDownloads"
								class="text-xl text-gray-900"
								:key="index"
							>
								<Link :href="`/products/${ item.product_id }/edit`">
									- {{ item.product_name }}, {{ item.amount }} st
								</Link>
							</p>

							<div class="absolute bottom-0 inset-x-0 bg-gray-50 px-4 py-4 sm:px-6 justify-end">
								<div class="text-sm">
									<a href="/products" class="font-medium text-[#45618a] hover:text-[#142b4c]">
										Till produktlistan »
										<span class="sr-only"> {{ __('Products') }}</span>
									</a>
								</div>
							</div>
						</dd>
					</div>
				</dl>
			</div>
		</div>

		<div class="w-full mt-[20px] bg-white rounded-[8px] shadow p-4">
			<div v-if="ready">
				<div class="flex justify-between">
					<div>
						<h3 class="font-bold">{{ dataOvertime.header }}</h3>
					</div>

					<div>
						<select class="mr-[20px]" id="cat_type" @change="changeGraphDisplayType">
							<option value="orders">Antal ordrar</option>
							<option value="products">Antal beställda produkter</option>
							<option value="downloads">Antal nedladdningar</option>
						</select>
						<button class="btn-cta text-[16px]" @click="modalToggle">
							{{ __('Export') }}
						</button>
					</div>
				</div>

				<div class="flex flex-col max-w-[250px] mb-[20px]">
					<label class="mb-[10px]" for="cat_filter">Filtrera på kategori</label>

					<select
						id="cat_filter"
						name="cat_filter"
						@change="setCategoryFilter"
					>
						<option value="" selected="true">Alla kategorier</option>

						<optgroup
							v-for="parent in parentCategories"
							:key="parent"
							:label="parent.name"
						>
							<option
								v-for="child in parent.childs"
								:key="child"
								:value="child.id"
							>
								{{ child.name }}
							</option>
						</optgroup>
					</select>
				</div>

				<Line
					v-if="!loading"
					:chart-data="dataOvertime.chart"
					:chart-options="lineChartOpt"
					:width="1338"
					:height="600"
				/>
				<div v-else class="min-h-[300px] flex items-center justify-center">Laddar ... </div>

				<!-- Disabled lots of things here, since it makes no sense showing them on the dashboard
				<div class="w-full overflow-x-auto">
					
					<table
						v-if="dataOvertime.data && dataOvertime.data.length > 0"
						class="w-full whitespace-nowrap"
					>
						<tr v-if="dataOvertime.type === 'orders'" class="text-left font-bold">
							<th class="px-6 pb-4 pt-6">{{ __('Date') }}</th>
							<th class="px-6 pb-4 pt-6">{{ __('Category') }}</th>
							<th class="px-6 pb-4 pt-6 text-right">{{ __('Quantity') }}</th>
						</tr>
						<tr v-else-if="dataOvertime.type === 'products'" class="text-left font-bold">
							<th class="px-6 pb-4 pt-6">{{ __('Date') }}</th>
							<th class="px-6 pb-4 pt-6">{{ __('Category') }}</th>
							<th class="px-6 pb-4 pt-6">{{ __('Product name') }}</th>
							<th class="px-6 pb-4 pt-6">{{ __('Article name') }}</th>
							<th class="px-6 pb-4 pt-6 text-right">{{ __('Quantity') }}</th>
						</tr>
						<tr v-else class="text-left font-bold">
							<th class="px-6 pb-4 pt-6">{{ __('Product name') }}</th>
							<th class="px-6 pb-4 pt-6">{{ __('Category') }}</th>
							<th class="px-6 pb-4 pt-6">{{ __('Date') }}</th>
						</tr>

						<tr
							v-for="item in dataOvertime.data"
							:key="item.id"
							class="focus-within:bg-gray-100 hover:bg-gray-100"
						>
							<td v-if="dataOvertime.type === 'orders' || dataOvertime.type === 'products'" class="border-t">
								<Link
									class="flex items-center px-6 py-4"
									:href="`/orders/${ item.order_id }/edit`"
								>
									{{ item.created_at }}
								</Link>
							</td>
							<td v-else>{{ item.created_at }}</td>

							<td v-if="dataOvertime.type === 'orders' || dataOvertime.type === 'products'" class="border-t px-6 py-4 text-right">
								{{ item.quantity }}
							</td>

							<td v-if="dataOvertime.type === 'downloads'" class="border-t">
								<Link
									class="flex items-center px-6 py-4"
									:href="`/products/${ item.id }/edit`"
								>
									{{ item?.product_name }}
								</Link>
							</td>

							<td v-if="dataOvertime.type === 'downloads'" class="border-t px-6 py-4">
								{{ item.category?.name }}
							</td>

							<td v-if="dataOvertime.type === 'downloads'" class="border-t px-6 py-4">
								{{ item?.created_at }}
							</td>
						</tr>
					</table>
					<div v-else class="w-full border-t px-6 py-4">
						<span>Ingen data registrerad</span>
					</div>
				</div-->

				<!--CustomPagination
                    :totalPages="totalPages"
                    :perPage="pagination.perPage"
                    :currentPage="pagination.currentPage"
                    :resultTotal="dataOvertime.data.length"
                    :total="pagination.total"
					:maxVisibleButtons=10
                    @pagechanged="onPageChange"
				/-->
			</div>
			<div v-else class="p-4">Laddar statistik...</div>
		</div>

		<Modal
			v-if="modal.show"
			:show="modal.show"
			:closeBtn="true"
			:size="modal.size"
			@close="modalToggle"
		>
			<div v-if="!exporting">
				<h2 class="block text-[24px] text-[#1d3557] mb-[15px]" v-if="dataOvertime.type == 'orders' || dataOvertime.type == 'products'">Exporterar ordrar inkl. beställda produkter</h2>
				<h2 class="block text-[24px] text-[#1d3557] mb-[15px]" v-else>Exporterar nedladdningar</h2>
				<p class="text-[18px] mb-[20px]">Vänligen välj vad du vill exportera</p>

				<select
					v-model="exportCatSelected"
					id="exp_filter"
					name="exp_filter"
				>
					<option value="" selected="true">Alla kategorier</option>

					<optgroup
						v-for="parent in parentCategories"
						:key="parent"
						:label="parent.name"
					>
						<option
							v-for="child in parent.childs"
							:key="child"
							:value="child.id"
						>
							{{ child.name }}
						</option>
					</optgroup>
				</select>

				<div class="w-full flex items-center justify-end mt-[40px]">
					<button class="mr-[10px]" @click="modalToggle">Avbryt</button>
					<button class="btn-cta" @click="fetchExportData">Exportera</button>
				</div>
			</div>
			<div v-else>
				<div class="modal__exporting">Exporterar&#8230;</div>
			</div>
		</Modal>
	</div>
</template>

<script>
	import { Head, Link }    	  	from '@inertiajs/inertia-vue3'
	import { Doughnut, Line } 	    from 'vue-chartjs'
	import { Chart, registerables } from 'chart.js'
	import Layout 		   		  	from '@/Shared/Layout'
	import CustomPagination 	  	from '@/Shared/CustomPagination'
	import Modal 	  				from '@/Shared/Modal'

	Chart.register(...registerables)

	export default {
		components: {
			Head,
			Link,
			Doughnut,
			Line,
			CustomPagination,
			Modal
		},
		layout: Layout,
		data: () => ({
			exporting: false,
			loading: false,
			ready: false,
			modal: {
                show: false,
                data: null,
                size: 'small'
			},
			pagination: {
				currentPage: 1,
				perPage: 12,
				skip: 0,
				total: 0
			},
			exportCatSelected: '',
			categories: [],
			dataOvertime: {
				type: 'orders',
				cat: '',
				chart: {
					labels: [],
					datasets: [ { data: [] } ]
				},
				data: null,
				description: 'Här kan du se det totala antalet ordar per månad.',
				header: 'Ordrar per månad'
			},
			topProducts: [],
			topProductsDays: 7,
			ordersOvertime: [],
			topDownloads: [],
			topDownloadsDays: 7,
			topDownloadsChartData: {
				chart: {
					labels: [],
					datasets: [{
						backgroundColor: ['#77CEFF', '#0079AF', '#123E6B', '#97B0C4', '#A5C8ED'],
						data: []
					}],
				},
				options: {
      				responsive: true,
      				maintainAspectRatio: false,
					plugins: {
						legend: {
							position: 'top',
						},
						title: {
							display: false
						}
					}
				}
			},
			topProductsChartData: {
				chart: {
					labels: [],
					datasets: [{
						backgroundColor: ['#77CEFF', '#0079AF', '#123E6B', '#97B0C4', '#A5C8ED'],
						data: []
					}],
				},
				options: {
      				responsive: true,
      				maintainAspectRatio: false,
					plugins: {
						legend: {
							position: 'top',
						},
						title: {
							display: false
						}
					}
				}
			},
			months: ['', 'Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'],
      		lineChartData: {
        		labels: [],
        		datasets: [ { data: [] } ],
      		},
			lineChartOpt: {
				borderColor: '#27a0fc',
				plugins: {
					legend: { display: false }
				}
			}
		}),
		computed: {
			totalPages() {
				return Math.ceil(this.pagination.total / this.pagination.perPage)
			},
			parentCategories() {
				const items = []

				this.categories.forEach(item => {
					if (!item.parent_id) {
						items.push(item)
					}
				})

				if (items.length > 0) {
					const childs = this.categories.filter(c => c.parent_id !== null)

					items.forEach(parent => {
						parent['childs'] = childs.filter(c => c.parent_id === parent.id)
						parent.childs.unshift(parent)
					})
				}

				return items
			},
			ChildCategories() {
				return this.categories.filter(c => c.parent_id !== null)
			}
		},
		methods: {
			initTopDownloadsChartData() {
				this.topDownloadsChartData.chart.labels = this.topDownloads.map(item => item.product_name)
				this.topDownloadsChartData.chart.datasets[0].data = this.topDownloads.map(item => item.amount)
			},
			initTopProductsChartData() {
				this.topProductsChartData.chart.labels = this.topProducts.map(item => item.name)
				this.topProductsChartData.chart.datasets[0].data = this.topProducts.map(item => item.amount)
			},
			catchPaginationClick(page) {
				this.fetchDataOvertime({
					type: this.dataOvertime.type,
					page: page
				})
			},
			setCategoryFilter(event) {
				this.dataOvertime.cat = event.target.value || null

				this.fetchDataOvertime({
					type: this.dataOvertime.type,
					cat: this.dataOvertime.cat
				})
			},
			initLineChart(data) {
				// Make sure to empty the current data before assigning new
				this.dataOvertime.chart.datasets[0].data = []

				if (data['chartData'] && data['chartData'].length > 0) {
					this.dataOvertime.chart.labels 			 = data['chartData'].map(item => `${ this.months[item.month] } ${ item.year }`)
					this.dataOvertime.chart.datasets[0].data = data['chartData'].map(item => Number(item.amount))
				}
			},
			setTopProductsDays(days) {
				this.topProductsDays = days
				this.fetchTopProducts()
			},
			setTopDownloadsDays(days) {
				this.topDownloadsDays = days
				this.fetchTopDownloads()
			},
			fetchTopProducts() {
				fetch('/api/top-products?days=' + this.topProductsDays)
					.then(response => response.json())
					.then(data => {
						this.topProducts = data
						this.initTopProductsChartData()
					})
			},
			fetchTopDownloads() {
				fetch('/api/top-downloads?days=' + this.topDownloadsDays)
					.then(response => response.json())
					.then(data => {
						this.topDownloads = data
						this.initTopDownloadsChartData()
					})
			},
			changeGraphDisplayType(event) {
				this.dataOvertime.type = event.target.value

				switch(this.dataOvertime.type) {
					case 'order':
						this.dataOvertime.header = 'Ordrar per månad';	
						this.dataOvertime.description = 'Här kan du se det totala antalet ordar per månad.';
						break;
					case 'products':
					this.dataOvertime.header = 'Beställda produkter per månad';
						this.dataOvertime.description = 'Här kan du se det totala antalet produkter som har beställts per månad.';
						break;
					case 'downloads':
						this.dataOvertime.header = 'Nedladdningar per månad';
						this.dataOvertime.description = 'Här kan du se det totala antalet produkter som har laddats ner per månad.';
						break;
				}

				this.fetchDataOvertime({
					type: this.dataOvertime.type,
					cat: this.dataOvertime.cat
				})
			},
			async fetchDataOvertime(filter) {
				const endp = new URL(`${ location.origin }/api/top-overtime`)

				if (filter?.type) {
					this.dataOvertime.type = filter.type
					endp.searchParams.append('type', filter.type)
				}

				if (filter?.page) {
					endp.searchParams.append('page', filter.page)
				}

				if (filter?.cat) {
					endp.searchParams.append('category', filter.cat)
				}

				// Pagination
				if (filter?.skip) {
					endp.searchParams.append('skip', filter.skip)
				}

				if (filter?.perPage) {
					endp.searchParams.append('per_page', filter.perPage)
				}

				this.loading = true

				await fetch(endp)
					.then(response => response.json())
					.then(data => {
						this.initLineChart(data)

						this.dataOvertime.data = data.pagination.data
						this.pagination.total  = data.pagination.total
						this.ready = true
						this.loading = false
					})
			},
			fetchCategories() {
				fetch('/api/categories?per_page=all').then((response) => response.json()).then(data => {
					this.categories = data.categories
				})
			},
			fetchExportData() {
				const endp = new URL(`${ location.origin }/api/export-overtime`)

				endp.searchParams.append('type', this.dataOvertime.type)

				if (this.exportCatSelected) {
					endp.searchParams.append('category', this.exportCatSelected)
				}

				this.exporting = true

				fetch(endp).then((response) => response.json()).then(data => {
					if (data.op === 'ok') {
						let today = new Date()

						const dd   = String(today.getDate()).padStart(2, '0')
                        const mm   = String(today.getMonth() + 1).padStart(2, '0')
                        const yyyy = today.getFullYear()

                        today = `export-${ yyyy }-${ mm }-${ dd }.xls`

						const link = document.createElement('a')

						link.href = data.file
						link.setAttribute('download', today)

						document.body.appendChild(link)

						link.click()
						link.remove()
					}

					this.exporting = false
				})
			},
			onPageChange(page) {
				this.pagination.currentPage = page

				this.fetchDataOvertime({
					type: this.dataOvertime.type,
					cat: this.dataOvertime.cat,
					skip: this.pagination.perPage * (page - 1),
					perPage: this.pagination.perPage
				})
			},
			modalToggle() {
				this.modal.show = !this.modal.show
			}
		},
		mounted() {
			this.fetchTopDownloads()
			this.fetchTopProducts()
			this.fetchCategories()
			this.fetchDataOvertime({
				type: this.dataOvertime.type,
				skip: this.pagination.skip,
				perPage: this.pagination.perPage
			})
		}
	}
</script>
